'use client'

import * as React from 'react'
import Image from 'next/image'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import { funnelTemplates, type FunnelTemplate } from '@/data/funnelTemplates'
import { Check, ArrowRight, Layers, FileText } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'

type CommunityTemplate = {
  template_id: number
  name: string
  description?: string
  thumbnail?: string | null
  tags?: string[]
  snapshot?: any
}

export type TemplatePickerTab = 'official' | 'community'

function getPageSections(page: any) {
  if (Array.isArray(page?.rows)) {
    return page.rows.map((row: any) => ({
      name: row?.name || 'Sección',
      count: Array.isArray(row?.columns)
        ? row.columns.reduce((acc: number, col: any) => {
            return acc + (Array.isArray(col?.modules) ? col.modules.length : 0)
          }, 0)
        : 0,
    }))
  }

  if (Array.isArray(page?.sections)) {
    return page.sections.map((section: any) => ({
      name: section?.name || 'Sección',
      count: Array.isArray(section?.blocks) ? section.blocks.length : 0,
    }))
  }

  return []
}

function getTotalBlocks(template: any) {
  return (template?.pages || []).reduce((acc: number, p: any) => {
    const sections = getPageSections(p)
    return acc + sections.reduce((sAcc: number, s: any) => sAcc + (s.count || 0), 0)
  }, 0)
}

export function TemplatePickerModal({
  open,
  onOpenChange,
  onCreated,
  initialTab = 'official',
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  onCreated: (funnelId: number) => void
  initialTab?: TemplatePickerTab
}) {
  const [tab, setTab] = React.useState<TemplatePickerTab>(initialTab)
  const [selectedTemplate, setSelectedTemplate] = React.useState<string | null>(null)
  const [previewOfficial, setPreviewOfficial] = React.useState<FunnelTemplate | null>(null)
  const [previewCommunity, setPreviewCommunity] = React.useState<CommunityTemplate | null>(null)
  const [communityTemplates, setCommunityTemplates] = React.useState<CommunityTemplate[]>([])
  const [loadingCommunity, setLoadingCommunity] = React.useState(false)
  const [creating, setCreating] = React.useState(false)
  const [name, setName] = React.useState('')
  const [query, setQuery] = React.useState('')

  React.useEffect(() => {
    if (!open) return

    setTab(initialTab)
    setQuery('')
    setSelectedTemplate(null)
    setPreviewOfficial(null)
    setPreviewCommunity(null)
    setName('')
  }, [open, initialTab])

  React.useEffect(() => {
    if (!open) return

    if (tab === 'official') {
      setPreviewCommunity(null)
      if (!previewOfficial && funnelTemplates.length) {
        setPreviewOfficial(funnelTemplates[0])
        setSelectedTemplate(funnelTemplates[0].id)
        setName(funnelTemplates[0].name)
      }
      return
    }

    setPreviewOfficial(null)
    setLoadingCommunity(true)

    fetch('/api/funnels/templates', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const list = Array.isArray(data?.templates) ? data.templates : []
        setCommunityTemplates(list)

        if (list.length) {
          setPreviewCommunity(list[0])
          setSelectedTemplate(String(list[0].template_id))
          setName(list[0].name || '')
        } else {
          setPreviewCommunity(null)
          setSelectedTemplate(null)
          setName('')
        }
      })
      .catch(() => {
        setCommunityTemplates([])
        setPreviewCommunity(null)
      })
      .finally(() => setLoadingCommunity(false))
  }, [open, tab])

  const filteredOfficial = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return funnelTemplates

    return funnelTemplates.filter((template) => {
      return (
        template.name.toLowerCase().includes(q) ||
        template.description.toLowerCase().includes(q) ||
        template.category.toLowerCase().includes(q) ||
        template.tags.some((tag) => tag.toLowerCase().includes(q))
      )
    })
  }, [query])

  const filteredCommunity = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return communityTemplates

    return communityTemplates.filter((template) => {
      return (
        String(template.name || '').toLowerCase().includes(q) ||
        String(template.description || '').toLowerCase().includes(q) ||
        (template.tags || []).some((tag) => String(tag).toLowerCase().includes(q))
      )
    })
  }, [communityTemplates, query])

  const previewTemplate = tab === 'official' ? previewOfficial : previewCommunity

  const handleUseTemplate = async () => {
    if (!previewTemplate) return

    setCreating(true)

    try {
      if (tab === 'official') {
        const t = previewTemplate as FunnelTemplate
        const res = await fetch('/api/funnels/templates/official/use', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            templateId: t.id,
            name: name.trim() || t.name,
          }),
        })

        const data = await res.json()
        if (!res.ok || !data?.ok) throw new Error(data?.error || 'Error creando embudo')

        toast.success(`Embudo "${name.trim() || t.name}" creado en modo borrador`)
        onOpenChange(false)
        setSelectedTemplate(null)
        setPreviewOfficial(null)
        setPreviewCommunity(null)
        setName('')
        onCreated(Number(data.funnelId))
        return
      }

      const t = previewTemplate as CommunityTemplate
      const res = await fetch(`/api/funnels/templates/${t.template_id}/use`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim() || t.name || 'Nuevo embudo',
          copyAssets: true,
        }),
      })

      const data = await res.json()
      if (!res.ok || !data?.ok) throw new Error(data?.error || 'Error creando embudo')

      toast.success(`Embudo "${name.trim() || t.name}" creado en modo borrador`)
      onOpenChange(false)
      setSelectedTemplate(null)
      setPreviewOfficial(null)
      setPreviewCommunity(null)
      setName('')
      onCreated(Number(data.funnelId))
    } catch (e: any) {
      toast.error(e?.message || 'Error')
    } finally {
      setCreating(false)
    }
  }

  const renderOfficialCard = (template: FunnelTemplate) => (
    <button
      key={template.id}
      onClick={() => {
        setSelectedTemplate(template.id)
        setPreviewOfficial(template)
        setName(template.name)
      }}
      className={cn(
        'group relative text-left rounded-xl border-2 p-5 transition-all duration-200 hover:shadow-lg',
        selectedTemplate === template.id
          ? 'border-primary bg-primary/5 shadow-md'
          : 'border-border hover:border-primary/40'
      )}
    >
      {selectedTemplate === template.id && (
        <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-primary flex items-center justify-center">
          <Check className="h-3.5 w-3.5 text-primary-foreground" />
        </div>
      )}

      <div className="flex items-start gap-4">
        <div
          className={cn(
            'flex-shrink-0 h-14 w-14 rounded-xl bg-gradient-to-br flex items-center justify-center text-2xl shadow-inner',
            template.gradient
          )}
        >
          {template.icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm mb-1 truncate">{template.name}</h3>
          <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
            {template.description}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            {template.tags.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </button>
  )

  const renderCommunityCard = (template: CommunityTemplate) => (
    <button
      key={template.template_id}
      onClick={() => {
        setSelectedTemplate(String(template.template_id))
        setPreviewCommunity(template)
        setName(template.name || '')
      }}
      className={cn(
        'group relative text-left rounded-xl border-2 p-5 transition-all duration-200 hover:shadow-lg',
        selectedTemplate === String(template.template_id)
          ? 'border-primary bg-primary/5 shadow-md'
          : 'border-border hover:border-primary/40'
      )}
    >
      {selectedTemplate === String(template.template_id) && (
        <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-primary flex items-center justify-center">
          <Check className="h-3.5 w-3.5 text-primary-foreground" />
        </div>
      )}

      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 h-14 w-14 rounded-xl bg-muted flex items-center justify-center text-2xl shadow-inner overflow-hidden relative">
          {template.thumbnail ? (
            <Image
              src={template.thumbnail}
              alt={template.name || 'Template'}
              fill
              className="object-cover"
            />
          ) : (
            '🧩'
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm mb-1 truncate">{template.name}</h3>
          <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
            {template.description || 'Plantilla guardada por la comunidad'}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            {(template.tags || []).map((tag) => (
              <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </button>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] p-0 gap-0 overflow-hidden">
        <DialogHeader className="p-6 pb-4 border-b border-border">
          <DialogTitle className="text-xl font-bold">Plantillas de Embudos</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Selecciona una plantilla profesional y personalízala a tu gusto
          </p>

          <div className="flex items-center gap-2 pt-4">
            <Button
              type="button"
              size="sm"
              variant={tab === 'official' ? 'default' : 'outline'}
              onClick={() => {
                setTab('official')
                setQuery('')
              }}
            >
              Oficiales
            </Button>

            <Button
              type="button"
              size="sm"
              variant={tab === 'community' ? 'default' : 'outline'}
              onClick={() => {
                setTab('community')
                setQuery('')
              }}
            >
              Comunidad
            </Button>
          </div>

          <div className="pt-3">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                tab === 'official'
                  ? 'Buscar plantillas oficiales...'
                  : 'Buscar plantillas de comunidad...'
              }
            />
          </div>
        </DialogHeader>

        <div className="flex h-[calc(85vh-14rem)] min-h-0 overflow-hidden">
          {/* Template List */}
          <ScrollArea
            className={cn(
              'transition-all duration-300',
              previewTemplate ? 'w-1/2 border-r border-border' : 'w-full'
            )}
          >
            <div
              className={cn(
                'grid gap-4 p-6',
                previewTemplate ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'
              )}
            >
              {tab === 'official' &&
                filteredOfficial.map((template) => renderOfficialCard(template))}

              {tab === 'community' &&
                (loadingCommunity ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="rounded-xl border-2 p-5">
                      <div className="flex items-start gap-4">
                        <div className="h-14 w-14 rounded-xl bg-muted animate-pulse" />
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="h-4 w-2/3 bg-muted rounded animate-pulse" />
                          <div className="h-3 w-full bg-muted rounded animate-pulse" />
                          <div className="h-3 w-4/5 bg-muted rounded animate-pulse" />
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  filteredCommunity.map((template) => renderCommunityCard(template))
                ))}
            </div>
          </ScrollArea>

          {/* Preview Panel */}
          {previewTemplate && (
            <div className="w-1/2 flex flex-col min-h-0">
              <ScrollArea className="flex-1 min-h-0">
                <div className="p-6">
                  {/* Header */}
                  <div className="mb-6">
                    <div
                      className={cn(
                        'h-20 w-20 rounded-2xl bg-gradient-to-br flex items-center justify-center text-4xl shadow-lg mb-4',
                        tab === 'official'
                          ? (previewTemplate as FunnelTemplate).gradient
                          : 'from-muted to-muted'
                      )}
                    >
                      {tab === 'official' ? (previewTemplate as FunnelTemplate).icon : '🧩'}
                    </div>

                    <h2 className="text-lg font-bold mb-1">
                      {tab === 'official'
                        ? (previewTemplate as FunnelTemplate).name
                        : (previewTemplate as CommunityTemplate).name}
                    </h2>

                    <p className="text-sm text-muted-foreground">
                      {tab === 'official'
                        ? (previewTemplate as FunnelTemplate).description
                        : (previewTemplate as CommunityTemplate).description ||
                          'Plantilla guardada por la comunidad'}
                    </p>
                  </div>

                  {/* Pages */}
                  {tab === 'official' ? (
                    <>
                      <div className="space-y-4">
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                          <Layers className="h-3.5 w-3.5" />
                          Páginas incluidas ({(previewTemplate as FunnelTemplate).pages.length})
                        </h3>

                        {(previewTemplate as FunnelTemplate).pages.map((page: any, index: number) => {
                          const sections = getPageSections(page)

                          return (
                            <div key={index} className="rounded-lg border border-border p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium text-sm">{page.name}</span>
                                <Badge variant="outline" className="text-[10px] ml-auto">
                                  {page.type}
                                </Badge>
                              </div>

                              <div className="space-y-1.5">
                                {sections.map((section: any, sIdx: number) => (
                                  <div
                                    key={sIdx}
                                    className="flex items-center gap-2 text-xs text-muted-foreground pl-2"
                                  >
                                    <div className="h-1.5 w-1.5 rounded-full bg-primary/50" />
                                    <span>{section.name}</span>
                                    <span className="text-[10px] opacity-60">
                                      ({section.count} bloque{section.count !== 1 ? 's' : ''})
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )
                        })}
                      </div>

                      <div className="mt-4 p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                        Total:{' '}
                        {getTotalBlocks(previewTemplate)} bloques listos para personalizar
                      </div>
                    </>
                  ) : (
                    <div className="mt-4 p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                      Esta plantilla de comunidad se duplicará en modo borrador para que puedas editarla antes de publicarla.
                    </div>
                  )}
                </div>
              </ScrollArea>

              {/* Action Button */}
              <div className="p-4 border-t border-border space-y-3 bg-background sticky bottom-0">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nombre del nuevo embudo"
                />

                <Button
                  className="w-full gap-2"
                  size="lg"
                  onClick={handleUseTemplate}
                  disabled={creating}
                >
                  {creating ? 'Creando...' : 'Usar esta plantilla'}
                  {!creating && <ArrowRight className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}